/**
 * useAdminTable — Hook réutilisable pour toutes les tables admin paginées.
 *
 * Gère search/filters/sort/pagination + reload + état "archivé".
 *
 * Usage :
 *   const t = useAdminTable("/admin/contacts", { sort: "created_at", order: "desc" });
 *   t.items, t.total, t.page, t.setPage, t.setQ, t.setFilters({source: "member"}), t.reload()
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

export function useAdminTable(endpoint, defaults = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [q, setQ] = useState("");
  const [filters, setFiltersState] = useState({});
  const [sort, setSort] = useState(defaults.sort || "created_at");
  const [order, setOrder] = useState(defaults.order || "desc");
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(false);

  const setFilters = useCallback((patch) => {
    setFiltersState((s) => ({ ...s, ...patch }));
    setPage(1);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      q: q || undefined,
      sort,
      order,
      page,
      per_page: perPage,
      archived,
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null)),
    };
    api.get(endpoint, { params })
      .then((r) => {
        // Backward-compat: support both {items, total} and bare arrays
        if (Array.isArray(r.data)) {
          setItems(r.data); setTotal(r.data.length); setPages(1);
        } else {
          setItems(r.data.items || []);
          setTotal(r.data.total || 0);
          setPages(r.data.pages || 0);
        }
      })
      .catch(() => { setItems([]); setTotal(0); setPages(0); })
      .finally(() => setLoading(false));
  }, [endpoint, q, sort, order, page, perPage, archived, filters]);

  // Debounce search (300ms)
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const toggleSort = (col) => {
    if (sort === col) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(col); setOrder("desc"); }
    setPage(1);
  };

  return {
    items, total, pages, page, perPage, q, sort, order, archived, loading, filters,
    setPage, setPerPage, setQ, setFilters, setSort, setOrder, setArchived, toggleSort,
    reload: load,
  };
}
